import { useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { JobEditor } from "@/components/jobs/job-editor";
import { useDict } from "@/lib/i18n/context";
import type { Job } from "@/lib/types";

export function JobDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const t = useDict();
  const [job, setJob] = useState<Job | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    if (!id) return;
    let active = true;
    setLoading(true);
    setError(null);
    setNotFound(false);
    fetch(`/api/jobs/${id}`, { credentials: "include" })
      .then(async (res) => {
        if (res.status === 404) {
          if (active) setNotFound(true);
          return null;
        }
        if (!res.ok) throw new Error("Failed to load");
        return res.json() as Promise<{ job: Job }>;
      })
      .then((data) => {
        if (active && data) setJob(data.job);
      })
      .catch(() => {
        if (active) setError("Failed to load");
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [id]);

  if (loading) {
    return (
      <div className="flex min-h-[40vh] items-center justify-center text-muted">
        Loading…
      </div>
    );
  }
  if (notFound) {
    return (
      <div className="flex min-h-[40vh] items-center justify-center text-muted">
        Not found
      </div>
    );
  }
  if (error || !job) {
    return (
      <div className="flex min-h-[40vh] items-center justify-center text-muted">
        {error || "Failed to load"}
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      <Link
        to="/jobs"
        className="inline-flex items-center gap-1 text-sm text-muted hover:text-foreground"
      >
        <ArrowLeft className="h-4 w-4 rtl:rotate-180" /> {t.jobs.backToJobs}
      </Link>
      <JobEditor job={job} initialEditing={searchParams.get("edit") === "1"} />
    </div>
  );
}
